import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AuthInput from './AuthInput';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { apiRegister } from '../../services/api';

const { FiMail, FiLock, FiUser, FiArrowRight, FiChevronLeft } = FiIcons;

export default function SignUpForm({ onAuth, onLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Name is required';
    if (!email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Enter a valid email';
    if (!password) e.password = 'Password is required';
    else if (password.length < 8) e.password = 'At least 8 characters';
    if (confirm !== password) e.confirm = 'Passwords do not match';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setLoading(true);
    try {
      const { user } = await apiRegister(email.trim().toLowerCase(), password);
      if (name.trim()) localStorage.setItem('glowlytix_display_name', name.trim());
      onAuth(user);
    } catch (err) {
      const msg = err.message || 'Registration failed. Please try again.';
      if (msg.toLowerCase().includes('password')) {
        setErrors({ password: msg });
      } else if (msg.toLowerCase().includes('email') || msg.toLowerCase().includes('account')) {
        setErrors({ email: msg });
      } else {
        setErrors({ email: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button onClick={onLogin} className="flex items-center gap-1 font-sans text-sm text-stone-400 mb-6 active:text-stone-600 transition-colors">
        <SafeIcon icon={FiChevronLeft} /> Back to sign in
      </button>

      <div className="mb-8">
        <h2 className="font-serif text-3xl text-stone-900 mb-1">Start your journey</h2>
        <p className="font-sans text-sm text-stone-400">Create your free Glowlytix account</p>
      </div>

      <AuthInput label="Your Name" type="text" value={name} onChange={setName} placeholder="Sophia" icon={FiUser} error={errors.name} />
      <AuthInput label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" icon={FiMail} error={errors.email} />
      <AuthInput label="Password" type="password" value={password} onChange={setPassword} placeholder="Min. 8 characters" icon={FiLock} error={errors.password} />
      <AuthInput label="Confirm Password" type="password" value={confirm} onChange={setConfirm} placeholder="••••••••" icon={FiLock} error={errors.confirm} />

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={handleSubmit}
        disabled={loading}
        className="w-full py-4 rounded-full font-sans font-semibold text-white text-base flex items-center justify-center gap-2 shadow-md mt-2 mb-6 disabled:opacity-70"
        style={{ background: 'linear-gradient(135deg, #C07A52, #E8C97A)' }}
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
        ) : (
          <><span>Create Account</span><SafeIcon icon={FiArrowRight} /></>
        )}
      </motion.button>

      <p className="text-center font-sans text-xs text-stone-400 leading-relaxed">
        By signing up, you agree to our{' '}
        <Link to="/terms" className="underline underline-offset-2 text-stone-500">Terms of Service</Link>
        {' '}and{' '}
        <Link to="/privacy" className="underline underline-offset-2 text-stone-500">Privacy Policy</Link>.
      </p>
      <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3">
        <p className="font-sans text-xs text-amber-700 leading-relaxed text-center">
          <strong>Not medical advice.</strong> Glowlytix is a personal tracking tool only. Nothing in this app — including AI-generated insights — constitutes medical advice, diagnosis, or treatment. Always consult a qualified dermatologist or healthcare professional for skin concerns.
        </p>
      </div>
    </div>
  );
}
